///
/// EarthBrowser - Window Utilities
///
/// Helper functions for window management
/// Created by Matt Giger on December 17, 2025
/// Copyright (c) 2025 Geodata Labs
///

import type { DatasetBrowserWindowState } from "@/redux/dialogSlice"
import { bringDatasetWindowToFront, dockWindowToDrawer, openDatasetWindow, openDrawer, setActiveDrawerTab, updateDatasetWindow } from "@/redux/dialogSlice"
import type { AppDispatch, RootState } from "@/redux/store"

// Default dimensions
const DEFAULT_WIDTH = 700
const DEFAULT_HEIGHT = 500
const MIN_INSET = 5

// Bottom margin to reserve space for the drawer tab bar and other UI elements
const BOTTOM_MARGIN = 20

// Calculate centered position for a new window
export function getCenteredPosition(width: number, height: number): { x: number; y: number } {
	const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920
	const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 1080

	// Constrain size to viewport with bottom margin
	const maxWidth = viewportWidth - (MIN_INSET * 2)
	const maxHeight = viewportHeight - BOTTOM_MARGIN - MIN_INSET
	const constrainedWidth = Math.min(width, maxWidth)
	const constrainedHeight = Math.min(height, maxHeight)

	return {
		x: Math.max(MIN_INSET, (viewportWidth - constrainedWidth) / 2),
		y: Math.max(MIN_INSET, (viewportHeight - BOTTOM_MARGIN - constrainedHeight) / 2),
	}
}

// Create a new window state object
// Note: Icons are NOT included here to keep Redux state serializable
export function createWindowState(
	id: string,
	title: string,
	options?: {
		width?: number
		height?: number
		x?: number
		y?: number
		dataSourceId?: string
		windowType?: "csv" | "shapefile" | "geojson" | "geopackage"
	}
): Omit<DatasetBrowserWindowState, 'zIndex'> {
	// Constrain dimensions to viewport with bottom margin
	const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920
	const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 1080
	const maxWidth = viewportWidth - (MIN_INSET * 2)
	const maxHeight = viewportHeight - BOTTOM_MARGIN - MIN_INSET

	const width = Math.min(options?.width ?? DEFAULT_WIDTH, maxWidth)
	const height = Math.min(options?.height ?? DEFAULT_HEIGHT, maxHeight)

	const position = options?.x !== undefined && options?.y !== undefined
		? { x: options.x, y: options.y }
		: getCenteredPosition(width, height)

	return {
		id,
		title,
		x: position.x,
		y: position.y,
		width,
		height,
		isMaximized: false,
		isVisible: true,
		dataSourceId: options?.dataSourceId,
		windowType: options?.windowType,
	}
}

// Generate a unique window ID
export function generateWindowId(prefix: string = 'window'): string {
	return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Get or create a dataset window for a file
 *
 * Checks if a window already exists for the given filename and type.
 * If it exists, returns null (indicating the file is already open).
 * If it doesn't exist, creates a new window and returns its ID.
 *
 * @param getState - Function to get current Redux state
 * @param dispatch - Redux dispatch function
 * @param filename - Name of the file
 * @param windowType - Type of window/browser to display
 * @param options - Optional width and height for the window
 * @returns Window ID if created, null if already exists
 */
export function getOrCreateDatasetWindow(
	getState: () => RootState,
	dispatch: AppDispatch,
	filename: string,
	windowType: "csv" | "shapefile" | "geojson" | "geopackage",
	options?: {
		width?: number
		height?: number
		/** Open the bottom drawer with the new tab (default true); story
		 *  playback mounts datasets without stealing the screen */
		openDrawer?: boolean
	}
): string | null {
	const state = getState()

	// Check if a window already exists for this file
	const existingWindow = Object.values(state.dialog.datasetWindows).find(
		window => window.title === filename && window.windowType === windowType
	)

	if (existingWindow) {
		console.log(`Window already exists for ${filename}, skipping`)
		return null
	}

	// Generate window ID
	const windowId = generateWindowId(windowType)

	// Create window immediately in loading state (centered if later undocked)
	const windowState = createWindowState(windowId, filename, {
		width: options?.width,
		height: options?.height,
		dataSourceId: windowId,
		windowType,
	})

	dispatch(openDatasetWindow(windowState))

	// Tables consolidate to the bottom drawer: dock new windows as drawer tabs
	dispatch(dockWindowToDrawer({ windowId, openDrawer: options?.openDrawer ?? true }))

	return windowId
}

/**
 * Open or restore a dataset window
 *
 * This is the recommended way to open dataset windows. It handles both cases:
 * - If the window doesn't exist, it creates a new one
 * - If the window already exists, it brings it to front (or activates its drawer tab)
 *
 * @param dispatch - Redux dispatch function
 * @param datasetWindows - Current dataset windows from Redux state
 * @param windowId - Unique identifier for the window
 * @param windowConfig - Configuration for the window (used when creating new)
 * @param updateOptions - Optional properties to update if window already exists
 * @returns void
 *
 * @example
 * ```ts
 * openOrRestoreWindow(
 *   dispatch,
 *   datasetWindows,
 *   'sql-terminal-browser',
 *   {
 *     id: 'sql-terminal-browser',
 *     title: 'SQL Terminal',
 *     windowType: 'sql-terminal',
 *     x: 150,
 *     y: 150,
 *     width: 900,
 *     height: 700,
 *     isMaximized: false,
 *     isVisible: true,
 *   }
 * )
 * ```
 */
export function constrainToViewport(
	config: Omit<DatasetBrowserWindowState, 'zIndex'>
): Omit<DatasetBrowserWindowState, 'zIndex'> {
	const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920
	const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 1080
	const maxWidth = viewportWidth - (MIN_INSET * 2)
	const maxHeight = viewportHeight - BOTTOM_MARGIN - MIN_INSET

	const width = Math.min(config.width, maxWidth)
	const height = Math.min(config.height, maxHeight)

	const maxX = viewportWidth - width - MIN_INSET
	const maxY = viewportHeight - BOTTOM_MARGIN - height
	const x = Math.max(MIN_INSET, Math.min(config.x, maxX))
	const y = Math.max(MIN_INSET, Math.min(config.y, maxY))

	return { ...config, x, y, width, height }
}

export function openOrRestoreWindow(
	dispatch: AppDispatch,
	datasetWindows: Record<string, DatasetBrowserWindowState>,
	windowId: string,
	windowConfig: Omit<DatasetBrowserWindowState, 'zIndex'>,
	updateOptions?: Partial<DatasetBrowserWindowState>
): void {
	const existingWindow = datasetWindows[windowId]

	if (existingWindow) {
		if (existingWindow.isDockedInDrawer) {
			dispatch(setActiveDrawerTab(windowId))
			dispatch(openDrawer())
		} else {
			if (updateOptions) {
				dispatch(updateDatasetWindow({
					id: windowId,
					updates: updateOptions,
				}))
			}
			dispatch(bringDatasetWindowToFront(windowId))
		}
	} else {
		dispatch(openDatasetWindow(constrainToViewport(windowConfig)))

		// Tables consolidate to the bottom drawer: dock new windows as drawer
		// tabs unless the window explicitly only supports floating mode.
		// Users can still drag a tab out to get a floating window.
		if (windowConfig.dockMode !== "window-only") {
			dispatch(dockWindowToDrawer({ windowId, openDrawer: true }))
		}
	}
}

